import React, { useEffect, useState } from "react";
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc, serverTimestamp, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { formatDate, formatPrice } from "../utils/format";
import { Plus, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import toast from "react-hot-toast";

export default function AdminCouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: "", type: "percentage", value: "", minOrderAmount: "", maxDiscount: "", usageLimit: "", active: true });

  async function load() {
    const snap = await getDocs(query(collection(db, "coupons"), orderBy("createdAt", "desc")));
    setCoupons(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  async function createCoupon(e) {
    e.preventDefault();
    if (!form.code || !form.value) return toast.error("Code and value required");
    await addDoc(collection(db, "coupons"), {
      code: form.code.trim().toUpperCase(),
      type: form.type,
      value: Number(form.value),
      minOrderAmount: form.minOrderAmount ? Number(form.minOrderAmount) : 0,
      maxDiscount: form.maxDiscount ? Number(form.maxDiscount) : null,
      usageLimit: form.usageLimit ? Number(form.usageLimit) : null,
      usedCount: 0,
      active: form.active,
      createdAt: serverTimestamp(),
    });
    toast.success("Coupon created!");
    setShowForm(false);
    setForm({ code: "", type: "percentage", value: "", minOrderAmount: "", maxDiscount: "", usageLimit: "", active: true });
    load();
  }

  async function toggleActive(id, current) {
    await updateDoc(doc(db, "coupons", id), { active: !current });
    load();
  }

  async function deleteCoupon(id, code) {
    if (!window.confirm(`Delete coupon "${code}"?`)) return;
    await deleteDoc(doc(db, "coupons", id));
    toast.success("Deleted");
    load();
  }

  return (
    <div style={{ padding: "40px 48px", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>Coupons</h1>
          <p style={{ color: "var(--text-muted)", fontSize: 15, marginTop: 4 }}>Create and manage discount codes.</p>
        </div>
        <button onClick={() => setShowForm(s => !s)} className="btn btn-primary" style={{ padding: "12px 20px", fontSize: 14 }}>
          {showForm ? "Cancel" : <><Plus size={18} /> Create Coupon</>}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ padding: 32, marginBottom: 32, background: "#fff", border: "1px solid var(--brand)", boxShadow: "var(--shadow-md)" }}>
          <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 18, marginBottom: 24 }}>New Coupon</h3>
          <form onSubmit={createCoupon} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 24 }}>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Coupon Code *</label>
              <input className="form-input" placeholder="e.g. SAVE20" value={form.code} onChange={set("code")} style={{ textTransform: "uppercase", padding: "12px 16px", borderRadius: 10, fontSize: 15 }} required />
            </div>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Type *</label>
              <select className="form-select" value={form.type} onChange={set("type")} style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }}>
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount (₦)</option>
              </select>
            </div>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Value * {form.type === "percentage" ? "(%)" : "(₦)"}</label>
              <input className="form-input" type="number" min={0} placeholder={form.type === "percentage" ? "20" : "5000"} value={form.value} onChange={set("value")} style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }} required />
            </div>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Min Order Amount (₦)</label>
              <input className="form-input" type="number" min={0} placeholder="10000" value={form.minOrderAmount} onChange={set("minOrderAmount")} style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }} />
            </div>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Max Discount (₦) — % only</label>
              <input className="form-input" type="number" min={0} placeholder="5000" value={form.maxDiscount} onChange={set("maxDiscount")} style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }} />
            </div>
            <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Usage Limit (blank = unlimited)</label>
              <input className="form-input" type="number" min={1} placeholder="100" value={form.usageLimit} onChange={set("usageLimit")} style={{ padding: "12px 16px", borderRadius: 10, fontSize: 15 }} />
            </div>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12, marginTop: 8, paddingTop: 24, borderTop: "1px solid var(--border)" }}>
              <button type="submit" className="btn btn-primary" style={{ padding: "12px 24px" }}>Create Coupon</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn btn-secondary" style={{ padding: "12px 24px" }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="card" style={{ overflow: "hidden", background: "#fff", border: "1px solid var(--border)" }}>
        {loading ? <div style={{ padding: 60, textAlign: "center" }}><div className="loader"><div className="spinner" /></div></div> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}>
                  {["Code", "Type", "Value", "Min Order", "Used", "Limit", "Status", "Created", ""].map(h => (
                    <th key={h} style={{ padding: "16px 20px", textAlign: "left", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {coupons.map(c => (
                  <tr key={c.id} style={{ borderBottom: "1px solid var(--border)", transition: "background 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "16px 20px", fontFamily: "monospace", fontWeight: 800, fontSize: 16, color: "var(--brand)" }}>{c.code}</td>
                    <td style={{ padding: "16px 20px", textTransform: "capitalize", fontWeight: 500 }}>{c.type}</td>
                    <td style={{ padding: "16px 20px", fontWeight: 700, fontSize: 15 }}>{c.type === "percentage" ? `${c.value}%` : formatPrice(c.value)}</td>
                    <td style={{ padding: "16px 20px", color: "var(--text-muted)", fontSize: 13 }}>{c.minOrderAmount ? formatPrice(c.minOrderAmount) : "—"}</td>
                    <td style={{ padding: "16px 20px", fontWeight: 600 }}>{c.usedCount || 0}</td>
                    <td style={{ padding: "16px 20px", color: "var(--text-muted)" }}>{c.usageLimit || "∞"}</td>
                    <td style={{ padding: "16px 20px" }}>
                      <button onClick={() => toggleActive(c.id, c.active)} style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: c.active ? "#10B981" : "var(--text-muted)", transition: "opacity 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.opacity = 0.7}
                        onMouseLeave={e => e.currentTarget.style.opacity = 1}>
                        {c.active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{c.active ? "Active" : "Inactive"}</span>
                      </button>
                    </td>
                    <td style={{ padding: "16px 20px", fontSize: 13, color: "var(--text-muted)" }}>{formatDate(c.createdAt)}</td>
                    <td style={{ padding: "16px 20px" }}>
                      <button onClick={() => deleteCoupon(c.id, c.code)} style={{ border: "none", color: "#EF4444", padding: 8, borderRadius: 6, background: "rgba(239, 68, 68, 0.1)", display: "flex", alignItems: "center", cursor: "pointer", transition: "background 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(239, 68, 68, 0.2)"}
                        onMouseLeave={e => e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)"}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && coupons.length === 0 && <p style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>No coupons yet. Create your first one above.</p>}
      </div>
    </div>
  );
}
